import React from 'react';
import { Button } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import './LandingPage.css';

const heroStats = [
    { value: 'OTP + MFA', label: 'secure participant access' },
    { value: 'End-to-end', label: 'voting lifecycle coverage' },
    { value: 'Audit ready', label: 'records and vote history' }
];

const workflow = [
    {
        title: 'Create the case voting room',
        description: 'Configure the resolution item, attach documents, and define the voting window for each IBC matter.'
    },
    {
        title: 'Invite CoC participants securely',
        description: 'Organizers can manage creditors, authorized representatives, and controlled access from one place.'
    },
    {
        title: 'Track every action transparently',
        description: 'Audit-ready records, vote confirmation, and case history help teams defend process integrity.'
    }
];

const highlights = [
    'Built for committee of creditors workflows',
    'Secure access with OTP and controlled voter journeys',
    'Structured vote rounds, documents, and result visibility',
    'Audit trail support for accountable decision making'
];

const featureGroups = [
    {
        title: 'Secure access and onboarding',
        description: 'Support sign-up, sign-in, OTP verification, password recovery, and controlled voter entry from invitation links.'
    },
    {
        title: 'Poll and resolution management',
        description: 'Create voting rooms, define agenda items, configure rounds, and keep each matter organized with attached context.'
    },
    {
        title: 'Participant and role control',
        description: 'Manage creditors, authorized representatives, voter lists, and account-level access across ongoing cases.'
    },
    {
        title: 'Voting experience and review',
        description: 'Guide voters through questions, review steps, confirmation pages, and representative-specific voting journeys.'
    },
    {
        title: 'Dashboard and visibility',
        description: 'Track poll status, participation progress, operational history, and decision outcomes from a central workspace.'
    },
    {
        title: 'Compliance-minded records',
        description: 'Maintain transparent timelines, action traces, and defensible reporting for review-heavy insolvency processes.'
    }
];

const roleViews = [
    'Insolvency professionals can launch and manage structured case voting.',
    'Creditors and representatives get guided participation with secure verification.',
    'Operations teams can monitor activity, profile details, and client-level context.'
];

function LandingPage() {
    const history = useHistory();
    const contactEmail = 'enquiries@enkindletech.com';
    const contactPhone = '+91 8041218544';

    const openContactEmail = (subject) => {
        const body = [
            'Hello TRUSTeVote team,',
            '',
            'I would like to know more about evoting.',
            'Number of voters:',
            '',
            'Please contact me to discuss the setup and quotation.',
            '',
            'Regards,'
        ].join('\n');

        window.location.href = `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    return (
        <div className="landing-page">
            <header className="landing-hero">
                <div className="landing-nav">
                    <div className="landing-brand">
                        {/* <img src="/eballot-dark.svg" alt="eBallot" className="landing-brand-logo" /> */}
                        <div>
                            <p className="landing-brand-kicker">IBC Voting Platform</p>
                            <h1>eBallot</h1>
                        </div>
                    </div>
                    <div className="landing-nav-actions">
                        <Button
                            variant="text"
                            className="landing-secondary-btn"
                            onClick={() => openContactEmail('Request Demo - eBallot')}
                        >
                            Request Demo
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            className="landing-primary-btn"
                            onClick={() => history.push('/sign-in')}
                        >
                            Sign In
                        </Button>
                    </div>
                </div>

                <div className="landing-hero-content">
                    <div className="landing-copy">
                        <p className="landing-eyebrow">Digital voting for insolvency decisions</p>
                        <h3>Run every CoC vote from one secure, transparent workspace.</h3>
                        <p className="landing-description">
                            Built for insolvency professionals and committee workflows, this platform supports secure
                            participation, structured voting rounds, and transparent records across every resolution process.
                        </p>
                        <div className="landing-cta-row">
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                className="landing-primary-btn"
                                onClick={() => history.push('/sign-in')}
                            >
                                Launch Voting Workspace
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                className="landing-outline-btn"
                                onClick={() => history.push('/sign-up')}
                            >
                                Create Account
                            </Button>
                        </div>
                        <div className="landing-hero-stats">
                            {heroStats.map((item) => (
                                <div key={item.value} className="landing-stat-card">
                                    <strong>{item.value}</strong>
                                    <span>{item.label}</span>
                                </div>
                            ))}
                        </div>
                        <div className="landing-trust-grid">
                            {highlights.map((item) => (
                                <div key={item} className="landing-trust-card">
                                    <span className="landing-trust-dot" />
                                    <p>{item}</p>
                                </div>
                            ))}
                        </div>
                        <p className="landing-contact-line">
                            Prefer to talk directly? Email <a href={`mailto:${contactEmail}`}>{contactEmail}</a> or call{' '}
                            <a href={`tel:${contactPhone}`}>{contactPhone}</a>
                        </p>
                    </div>

                    <div className="landing-visual-panel">
                        <div className="landing-hero-badge">Designed for IBC case coordination</div>
                        <div className="landing-status-card landing-status-primary">
                            <p className="landing-card-label">Case Snapshot</p>
                            <h3>CoC Resolution Voting</h3>
                            <p>Manage agenda items, creditors, and representatives with a single operational view.</p>
                        </div>
                        <div className="landing-status-card landing-status-secondary">
                            <div>
                                <p className="landing-card-label">Live Controls</p>
                                <strong>Voting round setup</strong>
                            </div>
                            <span>OTP secured</span>
                        </div>
                        <img
                            src="/images/mainpage.svg"
                            alt="Voting workflow illustration"
                            className="landing-illustration"
                        />
                        <div className="landing-status-card landing-status-tertiary">
                            <p className="landing-card-label">Platform Coverage</p>
                            <ul>
                                <li>Voting rooms and rounds</li>
                                <li>Participant verification</li>
                                <li>Review, cast, confirm</li>
                                <li>History and outcome visibility</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </header>

            <main className="landing-main">
                <section className="landing-section">
                    <div className="landing-section-heading">
                        <p>How it works</p>
                        <h3>Designed around real IBC voting operations</h3>
                    </div>
                    <div className="landing-workflow-grid">
                        {workflow.map((item, index) => (
                            <article key={item.title} className="landing-workflow-card">
                                <span className="landing-step-number">0{index + 1}</span>
                                <h4>{item.title}</h4>
                                <p>{item.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="landing-section">
                    <div className="landing-section-heading">
                        <p>Platform features</p>
                        <h3>Everything teams need to prepare, run, and review digital voting.</h3>
                    </div>
                    <div className="landing-feature-grid">
                        {featureGroups.map((item) => (
                            <article key={item.title} className="landing-feature-card">
                                <h4>{item.title}</h4>
                                <p>{item.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="landing-feature-banner">
                    <div>
                        <p className="landing-section-kicker">Why teams use it</p>
                        <h3>Every critical case action stays organized, guided, and reviewable.</h3>
                    </div>
                    <div className="landing-feature-points">
                        <p>Poll setup for resolutions, agenda items, and vote rounds</p>
                        <p>Participant management for creditors and authorized representatives</p>
                        <p>Centralized visibility into history, status, and vote completion</p>
                        <p>Account support flows for registration, verification, and password recovery</p>
                    </div>
                </section>

                <section className="landing-role-strip">
                    {roleViews.map((item) => (
                        <div key={item} className="landing-role-card">
                            <p>{item}</p>
                        </div>
                    ))}
                </section>

                <section className="landing-footer-cta">
                    <div>
                        <p className="landing-section-kicker">Ready to begin</p>
                        <h3>Start from the secure sign-in flow or request onboarding support.</h3>
                        <p className="landing-contact-line">
                            Contact us at <a href={`mailto:${contactEmail}`}>{contactEmail}</a> or{' '}
                            <a href={`tel:${contactPhone}`}>{contactPhone}</a>
                        </p>
                    </div>
                    <div className="landing-cta-row">
                        <Button
                            variant="contained"
                            color="primary"
                            className="landing-primary-btn"
                            onClick={() => history.push('/sign-in')}
                        >
                            Go to Sign In
                        </Button>
                    </div>
                </section>
            </main>
        </div>
    );
}

export default LandingPage;
